'use client';

import { c as _c } from "react/compiler-runtime";
import type * as nuqs from 'nuqs';
import { Button, ButtonLink, Icon, type IconName } from '~/components/primitives';
import type { ClassValue } from 'class-variance-authority/types';
import { Fragment, useMemo, type JSX, type ReactNode } from 'react';
import type { ts } from '@funkey/shared';
import { cx } from 'class-variance-authority';
import { useQueryStates } from 'nuqs';
import { useI18n } from '~/features/i18n';
import { useStateObject, type StateObject } from '~/utils/react';
import Title from '~/components/utils/Title';
import { usePortalCtx } from '~/features/global/portal';
import { ActivityFilterPopover } from '~/features/activities/ActivityFilterBar/components/ActivityFilterPopover';
import type { ActivityFilterConfig } from '~/features/activities/ActivityFilterBar';
type activityFilterConfigToStateType<T extends readonly any[]> = ts.Union.toIntersection<{ [Filter in T[number] as string]: Filter extends ActivityFilterConfig<infer U, any> ? U : never }[string]>;
export const ActivityFiltersLayout = props => {
  const $ = _c(38);
  const portal = usePortalCtx();
  const ButtonComponent = !props.button ? null : "href" in props.button ? ButtonLink : Button;
  let t0;
  if ($[0] !== portal || $[1] !== props.bare || $[2] !== props.className || $[3] !== props.dense || $[4] !== props.variant) {
    t0 = props.dense ? cx("flex h-11 w-fit max-w-full min-w-0 items-center rounded-full bg-white px-2 text-sm shadow-xs", props.className) : cx(!props.bare && props.variant === "pill" && "lg:rounded-5xl lg:bg-white lg:py-3 lg:pr-3 lg:pl-3", !props.bare && portal.portal === "WEB" && "shadow-2xl shadow-black/5", !props.bare && "rounded-2xl shadow-black/2 max-lg:shadow-none lg:overflow-hidden", props.className, "w-fit md:flex-row lg:flex lg:gap-3", props.bare && "lg:w-full", "grid auto-rows-auto grid-cols-2 text-sm max-lg:gap-2", "max-lg:mx-auto max-lg:w-full max-lg:max-w-xl");
    $[0] = portal;
    $[1] = props.bare;
    $[2] = props.className;
    $[3] = props.dense;
    $[4] = props.variant;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const menuClasses = t0;
  let t1;
  if ($[6] !== props.leading) {
    t1 = props.leading && <div className="max-lg:hidden lg:flex lg:items-center lg:self-stretch lg:pr-2">{props.leading}</div>;
    $[6] = props.leading;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props.bare || $[9] !== props.compact || $[10] !== props.dense || $[11] !== props.filters || $[12] !== props.hideMobileCategoriesFilter || $[13] !== props.mobileLayout || $[14] !== props.state || $[15] !== props.uniformMobileColSpans) {
    let t3;
    if ($[17] !== props.bare || $[18] !== props.compact || $[19] !== props.dense || $[20] !== props.filters.length || $[21] !== props.hideMobileCategoriesFilter || $[22] !== props.mobileLayout || $[23] !== props.state || $[24] !== props.uniformMobileColSpans) {
      t3 = (filter, idx) => <ActivityFilterItem key={filter.key} filter={filter} idx={idx} count={props.filters.length} state={props.state} dense={props.dense} compact={props.compact} bare={props.bare} hideMobileCategoriesFilter={props.hideMobileCategoriesFilter} mobileLayout={props.mobileLayout} uniformMobileColSpans={props.uniformMobileColSpans} />;
      $[17] = props.bare;
      $[18] = props.compact;
      $[19] = props.dense;
      $[20] = props.filters.length;
      $[21] = props.hideMobileCategoriesFilter;
      $[22] = props.mobileLayout;
      $[23] = props.state;
      $[24] = props.uniformMobileColSpans;
      $[25] = t3;
    } else {
      t3 = $[25];
    }
    t2 = props.filters.map(t3);
    $[8] = props.bare;
    $[9] = props.compact;
    $[10] = props.dense;
    $[11] = props.filters;
    $[12] = props.hideMobileCategoriesFilter;
    $[13] = props.mobileLayout;
    $[14] = props.state;
    $[15] = props.uniformMobileColSpans;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[26] !== props.mobileFooter) {
    t3 = props.mobileFooter != null && props.mobileFooter !== false && <div className={cx("col-span-2 self-start rounded-2xl bg-white px-4 py-3 lg:hidden", "flex w-full min-w-0 flex-wrap items-center gap-2")}>{props.mobileFooter}</div>;
    $[26] = props.mobileFooter;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  let t4;
  if ($[28] !== ButtonComponent || $[29] !== props.button || $[30] !== props.compact) {
    t4 = props.button && ButtonComponent && <ButtonComponent {...props.button as any} className={cx("col-span-2 whitespace-nowrap max-lg:h-14 max-lg:w-full max-lg:rounded-2xl max-lg:p-0!", props.compact && "lg:h-16")} variant="tertiary" size="lg">{props.button.label}</ButtonComponent>;
    $[28] = ButtonComponent;
    $[29] = props.button;
    $[30] = props.compact;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] !== menuClasses || $[33] !== t1 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4) {
    t5 = <menu className={menuClasses}>{t1}{t2}{t3}{t4}</menu>;
    $[32] = menuClasses;
    $[33] = t1;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  return t5;
};

/** One filter in the bar. Its own component because `useDataLoader` is a hook: called from
 *  inside the parent's `.map()` it would change the parent's hook count whenever the filter
 *  list changes, or whenever a filter without a loader sits next to one with it. */
const ActivityFilterItem = <S,>(props: {
  filter: ActivityFilterConfig<any, any>;
  idx: number;
  count: number;
  state: StateObject<S>;
  dense?: boolean;
  compact?: boolean;
  bare?: boolean;
  hideMobileCategoriesFilter?: boolean;
  mobileLayout?: 'default' | 'pair-outer';
  uniformMobileColSpans?: boolean;
}) => {
  const i18n = useI18n();
  const {
    filter,
    idx
  } = props;
  const data = filter.useDataLoader?.(props.state.value as Record<string, unknown>);
  const desktopSelection = filter.selectionDisplay?.(i18n, props.state.value, data, 'desktop');
  const footNote = filter.footNote?.(i18n);
  const isFirstOrLastElement = idx === 0 || idx === props.count - 1;
  const contentProps = {
    i18n,
    value: props.state.value,
    onChange: props.state.set,
    data
  };
  const desktopContent = <filter.PopupContent {...contentProps} />;
  // The summary says "20 participants", not a bare "20".
  const denseSelection = filter.key === 'participants' && desktopSelection && /^\d+$/.test(desktopSelection) ? i18n._('{count} participants', {
    count: Number(desktopSelection)
  }) : desktopSelection;
  return <Fragment>
            {props.dense && idx > 0 && <span aria-hidden className="h-4 w-px shrink-0 bg-primary/15 max-lg:hidden" />}
            {/* Desktop menu button */}
            <ActivityFilterPopover.Root>
                <ActivityFilterPopover.DesktopButton className={cx('max-lg:hidden', props.dense && 'mx-1',
      // Fixed width outside the bare bar: the row is
      // `w-fit`, so with `flex-1` every pill shared the
      // surplus max-content width and a longer location
      // silently widened its neighbours too.
      !props.dense && (props.bare ? 'flex-1' : props.compact ? 'lg:w-44' : 'lg:w-48'))} compact={props.compact} dense={props.dense ? {
        icon: filter.mobile.icon
      } : undefined} label={filter.label?.(i18n)} placeholder={filter.placeholder?.(i18n) ?? i18n._('No preference')} selected={props.dense ? denseSelection : desktopSelection} data-testid={`filter-${filter.key}`} />
                <ActivityFilterPopover.Content className={filter.popoverClassName}>
                    <Title size="base" weight="bold" className="mb-4">
                        {filter.label(i18n)}
                        {footNote && <FilterFootNote.Pointer />}
                    </Title>
                    {desktopContent}

                    {footNote && <FilterFootNote.Text>{footNote}</FilterFootNote.Text>}
                </ActivityFilterPopover.Content>
            </ActivityFilterPopover.Root>

            {!props.dense && !(props.hideMobileCategoriesFilter && filter.key === 'categories') && <div data-testid={`filter-${filter.key}`} className={cx(
    // Each filter is its own rounded white block; the
    // grid gap separates them visually.
    'overflow-hidden rounded-2xl bg-white lg:hidden', 'flex flex-col', props.mobileLayout === 'pair-outer' && props.count === 3 ? ['col-span-1 max-lg:order-1', 'col-span-2 max-lg:order-3', 'col-span-1 max-lg:order-2'][idx] : props.uniformMobileColSpans || isFirstOrLastElement || props.count === 3 ? 'col-span-2' : 'col-span-1')}>
                    <div
      // Whole card is the tap target: clicking the icon or empty
      // space forwards to the inner control (focus an input, click a
      // popover trigger) so the hit area isn't just the text line.
      // Keyboard users reach that control directly, so this is presentational.
      role="presentation" onClick={e => {
        if (!(e.target instanceof Element)) return;
        // Radix portals its popovers out of this card, but React
        // still bubbles their synthetic events through here. Without
        // this guard, picking a radius inside the location filter's
        // combobox refocuses the city input and reopens the search
        // on mobile.
        if (!e.currentTarget.contains(e.target)) return;
        if (e.target.closest('input, textarea, button, a, [role="button"]')) return;
        const control = e.currentTarget.querySelector('input, textarea, button, [role="button"]');
        if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
          control.focus();
        } else if (control instanceof HTMLElement) {
          control.click();
        }
      }} className={`relative flex min-h-14 w-full cursor-pointer items-center gap-4 border-0 bg-white px-4 py-2 transition-all select-none hover:bg-gray-100`}>
                        {/* The location filter's field renders its own pin, so skip
                                    the row icon to avoid a duplicate location icon on mobile. */}
                        {filter.key !== 'regions' && <Icon name={filter.mobile.icon} size="sm" strokeWidth={1.5} className={cx('pointer-events-none shrink-0',
        // Green only once the filter has a selection.
        filter.selectionDisplay?.(i18n, props.state.value, data, 'mobile') ? 'text-secondary' : 'text-primary')} />}
                        {filter.mobile.Component ? <filter.mobile.Component {...contentProps} /> : desktopContent}
                    </div>
                </div>}
        </Fragment>;
};
ActivityFiltersLayout.createDefaultValues = <T extends readonly ActivityFilterConfig<any, any>[],>(defaultValues: activityFilterConfigToStateType<T>) => defaultValues;
ActivityFiltersLayout.createStateHook = <T extends readonly ActivityFilterConfig<any, any>[],>(defaultValues: activityFilterConfigToStateType<T> & {}) => (defaultValueOverrides?: Partial<typeof defaultValues>) => useStateObject<activityFilterConfigToStateType<T>>({
  ...defaultValues,
  ...defaultValueOverrides
});
ActivityFiltersLayout.createQueryStateHook = <T extends nuqs.UseQueryStatesKeysMap,>(searchParamConfig: T) => () => {
  const [value, set] = useQueryStates(searchParamConfig);
  return useMemo(() => ({
    value,
    set
  }) as const, [value]); // prettier-ignore
};
namespace FilterFootNote {
  export const Pointer: React.FC = () => {
    const $ = _c(1);
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <span className="text-sm font-normal text-blue-400"> *</span>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  };
  export const Text: React.FC<{
    children: React.ReactNode;
  }> = props => {
    const $ = _c(2);
    let t0;
    if ($[0] !== props.children) {
      t0 = <p className="mt-8 text-sm font-normal text-blue-400">{props.children}</p>;
      $[0] = props.children;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  };
}